import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { ArrowLeft, MapPin, WhatsappLogo, FilePdf, MapTrifold, Phone } from "@phosphor-icons/react";
import { formatMonthYear } from "@/components/MonthYearPicker";

const NP = "Not Provided";
const clean = (v) => String(v ?? "").replace(/<[^>]*>/g, " ").replace(/(https?:\/\/|www\.)\S+/gi, "").replace(/[ \t]+/g, " ").trim();
const val = (v) => clean(v) || NP;
const linkOf = (v) => {
  const s = String(v || "");
  const src = s.match(/src=["']([^"']+)["']/i);
  const url = src ? src[1] : (s.match(/https?:\/\/[^\s"'<>]+/i) || [])[0];
  return url || null;
};

function Info({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`text-sm mt-1 ${value === NP ? "text-slate-500 italic" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-luxury p-6">
      <h3 className="font-serif text-lg text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Approval({ kind, p }) {
  const k = kind.toLowerCase();
  const cert = p[`${k}_certificate`];
  return (
    <div className="p-4 rounded-md bg-[#060B14] border border-[#1E293B] space-y-3" data-testid={`detail-${k}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">{kind}</div>
        <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37]">{val(p[`${k}_status`])}</span>
      </div>
      <div className={`grid ${kind === "RERA" ? "grid-cols-2" : "grid-cols-3"} gap-3`}>
        <Info label="Number" value={val(p[`${k}_number`])} />
        {kind !== "RERA" && <Info label="Authority" value={val(p[`${k}_authority`])} />}
        <Info label="Date" value={val(formatMonthYear(p[`${k}_date`]))} />
      </div>
      {cert?.path && <a href={fileUrl(cert.path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline"><FilePdf size={14} /> {kind} Certificate</a>}
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState(false);
  const [broken, setBroken] = useState([]);
  const markBroken = (src) => setBroken(b => (b.includes(src) ? b : [...b, src]));

  useEffect(() => {
    api.get(`/properties/${id}`).then(r => setP(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="text-slate-400">Property not found. <Link to="/properties" className="text-[#D4AF37]">Back to Inventory</Link></div>;
  if (!p) return <div className="text-slate-400">Loading…</div>;

  const images = [
    ...(p.cover_photo?.path ? [fileUrl(p.cover_photo.path)] : p.image_url ? [p.image_url] : []),
    ...(p.gallery || []).map(g => fileUrl(g.path)),
  ].filter(src => !broken.includes(src));
  const main = active && !broken.includes(active) ? active : images[0];
  const mapHref = linkOf(p.map_url);
  const brochureHref = linkOf(p.brochure_url);
  const waDigits = String(p.salesperson_phone || "").replace(/\D/g, "");
  const waNum = waDigits.length === 10 ? `91${waDigits}` : waDigits;
  const docs = [
    p.brochure_file?.path && { label: "Brochure", href: fileUrl(p.brochure_file.path) },
    brochureHref && { label: "Brochure Link", href: brochureHref },
    p.rera_certificate?.path && { label: "RERA Certificate", href: fileUrl(p.rera_certificate.path) },
    p.clu_certificate?.path && { label: "CLU Certificate", href: fileUrl(p.clu_certificate.path) },
  ].filter(Boolean);
  const shareText = [
    `*${p.project_name}*${p.property_type ? ` (${p.property_type})` : ""}`,
    [p.sector, p.location].filter(Boolean).join(", "),
    p.plot_size && `Sizes: ${p.plot_size}`,
    p.price && `Price: ₹${p.price}`,
    p.rera_status && `RERA: ${p.rera_status}${p.rera_number ? ` (${p.rera_number})` : ""}`,
    mapHref && `Location: ${mapHref}`,
    brochureHref && `Brochure: ${brochureHref}`,
    "— Nawab Properties",
  ].filter(Boolean).join("\n");

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      <Link to="/properties" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#D4AF37]" data-testid="back-to-inventory">
        <ArrowLeft size={14} /> Back to Inventory
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">{val(p.property_type)}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-1" data-testid="detail-project-name">{p.project_name}</h1>
          <div className="text-sm text-slate-400 mt-1 flex items-center gap-1"><MapPin size={14} weight="duotone" /> {[p.sector, p.location].filter(Boolean).join(" • ") || NP}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-[#D4AF37]/30 text-[#D4AF37]" data-testid="detail-availability">{val(p.availability)}</span>
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" data-testid="detail-whatsapp-share"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm hover:bg-emerald-500/20">
            <WhatsappLogo size={16} weight="duotone" /> Share on WhatsApp
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-luxury overflow-hidden">
          <div className="aspect-[16/9] bg-[#060B14]">
            {main ? <img src={main} alt="" onError={() => markBroken(main)} className="w-full h-full object-cover" data-testid="detail-main-image" />
              : <div className="w-full h-full flex items-center justify-center text-slate-500 italic">No photos provided</div>}
          </div>
          {images.length > 1 && (
            <div className="p-3 flex gap-2 overflow-x-auto" data-testid="detail-gallery">
              {images.map((src, i) => (
                <button key={i} onClick={() => setActive(src)} className={`w-20 h-14 shrink-0 rounded-md overflow-hidden border ${main === src ? "border-[#D4AF37]" : "border-[#1E293B]"}`}>
                  <img src={src} alt="" onError={() => markBroken(src)} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="card-luxury p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 pb-4 border-b border-[#1E293B]">
            <div data-testid="detail-selling-price">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Selling Price</div>
              <div className="font-serif text-2xl text-[#D4AF37] mt-1">{p.price ? `₹${p.price}` : <span className="text-sm text-slate-500 italic">{NP}</span>}</div>
            </div>
            <div data-testid="detail-onboard-price">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">On-Board Price</div>
              <div className="font-serif text-2xl text-white mt-1">{p.onboard_price ? `₹${p.onboard_price}` : <span className="text-sm text-slate-500 italic">{NP}</span>}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Info label="Plot Sizes / Types" value={val(p.plot_size)} testid="detail-plot-size" />
            <Info label="Owner / Developer" value={val(p.owner_name)} testid="detail-owner" />
            <Info label="Total Land Area" value={val(p.land_area)} testid="detail-land-area" />
            <Info label="Total Plots / Units" value={val(p.total_units)} testid="detail-units" />
            <Info label="Expected Possession" value={val(p.possession_date)} testid="detail-possession" />
            <Info label="Availability" value={val(p.availability)} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Project Description">
          <p className={`text-sm whitespace-pre-wrap leading-relaxed ${p.description ? "text-slate-300" : "text-slate-500 italic"}`} data-testid="detail-description">{val(p.description)}</p>
        </Section>
        <Section title="Amenities & Facilities">
          <p className={`text-sm whitespace-pre-wrap leading-relaxed ${p.amenities ? "text-slate-300" : "text-slate-500 italic"}`} data-testid="detail-amenities">{val(p.amenities)}</p>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Section title="Approvals">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Approval kind="RERA" p={p} />
              <Approval kind="CLU" p={p} />
            </div>
          </Section>
        </div>
        <Section title="Location & Documents">
          <div className="space-y-3" data-testid="detail-documents">
            {mapHref ? (
              <a href={mapHref} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#D4AF37] hover:underline" data-testid="detail-map-link"><MapTrifold size={16} weight="duotone" /> Open in Google Maps</a>
            ) : <div className="text-sm text-slate-500 italic">Map: {NP}</div>}
            {docs.length ? docs.map(d => (
              <a key={d.label} href={d.href} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-slate-200 hover:text-[#D4AF37]"><FilePdf size={16} weight="duotone" className="text-[#D4AF37]" /> {d.label}</a>
            )) : <div className="text-sm text-slate-500 italic">Documents: {NP}</div>}
          </div>
        </Section>
      </div>

      <Section title="Contact Person / Salesperson">
        <div className="flex items-center justify-between flex-wrap gap-4" data-testid="detail-salesperson">
          <div className="grid grid-cols-2 gap-6">
            <Info label="Name" value={val(p.salesperson_name)} testid="detail-salesperson-name" />
            <Info label="Contact Number" value={val(p.salesperson_phone)} testid="detail-salesperson-phone" />
          </div>
          {waDigits && (
            <div className="flex gap-2">
              <a href={`tel:${p.salesperson_phone}`} data-testid="detail-salesperson-call" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sky-500/10 border border-sky-500/30 text-sky-400 text-sm hover:bg-sky-500/20"><Phone size={16} weight="duotone" /> Call</a>
              <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" data-testid="detail-salesperson-whatsapp" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm hover:bg-emerald-500/20"><WhatsappLogo size={16} weight="duotone" /> WhatsApp</a>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
